/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼の差戻し履歴ボックス。差戻すたびに記録された日時・操作者・理由・依頼者へ送ったメッセージを新しい順に全文表示する。差戻したことの無い依頼では何も出さない。依頼詳細のメインカラムに出す
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { opsActivityTimeLabel, type OpsSendBack } from '@cortex/ops-core';
import { AccentBox } from './OpsPanels.js';

/** OpsSendBackHistoryBox の props。 */
export interface OpsSendBackHistoryBoxProps {
  /** 差戻し記録（古い順）。詳細レスポンス以外では undefined。 */
  sendBacks: OpsSendBack[] | undefined;
}

/**
 * 差戻し履歴ボックス。最新の差戻しが一番知りたい情報なので新しい順に並べる。時刻は活動ログと同じ JST 表記。
 * @graph-connects none
 */
export function OpsSendBackHistoryBox({ sendBacks }: OpsSendBackHistoryBoxProps): ReactNode {
  const { t } = useTranslation();
  if (sendBacks === undefined || sendBacks.length === 0) return null;
  return (
    <AccentBox tone="warning" title={t('ops.detail.sendBack.historyTitle')}>
      <ul className="space-y-3">
        {[...sendBacks].reverse().map((entry, index) => (
          <li key={`${entry.at}-${String(index)}`} className="space-y-0.5">
            <div className="flex flex-wrap items-center gap-x-2 text-xs text-[var(--color-text-muted)]">
              <span>
                {opsActivityTimeLabel({ time: entry.at, at: entry.at, actor: null, text: '' })}
              </span>
              <span>{entry.actor ?? t('ops.detail.sendBack.unknownActor')}</span>
              <span className="font-semibold text-[var(--color-text-secondary)]">
                {t(`ops.detail.sendBack.reasons.${entry.reason}`)}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
              {entry.message}
            </p>
          </li>
        ))}
      </ul>
    </AccentBox>
  );
}
